import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { z } from 'zod';
import { getApiUrl } from '@shared/config/env';
import { unwrapApiResponse } from './envelope';

// Puerta de `src/api/client.js` (legado, borrado en T2 de specs/contexts/shared-kernel/tasks.md).
// Mismo comportamiento observado en spec.md §2.1 / T-1 / T-2 — sin cambios de comportamiento.

const API_URL = getApiUrl();

export interface HttpAuthPolicy {
  readAccessToken(): string | null;
  readRefreshToken(): string | null;
  persistAccessToken(token: string): void;
  clearSession(): void;
}

let authPolicy: HttpAuthPolicy | null = null;

export function configureHttpAuthPolicy(policy: HttpAuthPolicy): void {
  authPolicy = policy;
}

function getAuthPolicy(): HttpAuthPolicy {
  if (!authPolicy) throw new Error('HTTP auth policy is not configured');
  return authPolicy;
}

/**
 * Endpoints que nunca disparan el flujo de refresh de token (login/registro y settings públicos).
 * Extraída como función pura para poder testearla sin mockear axios (spec.md §5).
 * v1 reorganizó auth bajo `/auth/password/*` y `/auth/session/*`, pero conserva el prefijo `/auth/`.
 */
export function isRefreshExemptUrl(url: string | undefined): boolean {
  return Boolean(url?.startsWith('/auth/') || url?.startsWith('/app-settings'));
}

const RefreshResponseSchema = z.object({ accessToken: z.string() });

export const httpClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor de request — adjunta el JWT si existe (mismo comportamiento que client.js:11-17).
httpClient.interceptors.request.use((config) => {
  const token = getAuthPolicy().readAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Desenvuelve el `ApiResponse` de v1 (mapping.md §S-1). Registrado antes del interceptor de 401
// para que sea el más interno: el reintento tras refresh entrega el payload ya desenvuelto.
// Las descargas binarias (`responseType: 'blob'`) no viajan envueltas y se dejan intactas.
httpClient.interceptors.response.use((response) => {
  if (response.config.responseType === 'blob') return response;
  return { ...response, data: unwrapApiResponse(response.data) };
});

// Promesa compartida del refresh en curso (o null si no hay ninguno). Evita que N peticiones
// que reciben 401 en paralelo (patrón común: hooks de application/ que hacen Promise.all,
// ver spec.md de este contexto) disparen N llamadas independientes a /auth/refresh — si el
// backend rota el refresh token de un solo uso, solo la primera de esas N tendría éxito y el
// resto invalidaría la sesión con el mismo "Token no válido." en bucle.
let refreshPromise: Promise<string> | null = null;

function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const policy = getAuthPolicy();
      const refreshToken = policy.readRefreshToken();
      if (!refreshToken) throw new Error('No refresh token');

      // La respuesta cruda de `/auth/session/refresh` es el único punto del kernel donde se
      // interpreta un payload HTTP sin pasar por un repositorio de negocio — se valida
      // con zod aquí (constitution.md regla #5, ver plan.md §2). Usa `axios.post` (instancia
      // nueva) para no reentrar en el interceptor de 401, así que desenvuelve el envelope a mano.
      const { data } = await axios.post<unknown>(`${API_URL}/auth/session/refresh`, { refreshToken });
      const { accessToken } = RefreshResponseSchema.parse(unwrapApiResponse(data));

      policy.persistAccessToken(accessToken);
      return accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Interceptor de response — maneja 401 con refresh de token (mismo comportamiento que
// client.js:20-56). El request de refresh usa `axios.post` (instancia nueva, no `httpClient`)
// para no reentrar en este mismo interceptor.
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (isRefreshExemptUrl(originalRequest?.url)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        getAuthPolicy().clearSession();
        // Solo redirige si no está ya en /login, para evitar un loop de redirección.
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default httpClient;
